import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { z } from 'zod';
import { PROFILE_MAX_AGE } from '@/constants/profile';
import { getLanguageTag } from '@/helpers/locale';
import { describeBirthDate, parseBirthDate, toApiBirthDate, validateBirthDate } from '@/helpers/profile-birth-date';
import { actionTypes } from '@/store/modules/profile/createProfile';
import { useProfileBirthDateField } from '@/views/profiles/ManageProfiles/composables/use-profile-birth-date-field';
import { DEFAULT_PROFILE_AVATAR } from '@/views/profiles/constants';

export const useAddProfileForm = () => {
  const { t, locale } = useI18n();
  const store = useStore();
  const router = useRouter();

  const requiredMessage = t('addProfileForm.errors.required');

  const validationSchema = toTypedSchema(
    z.object({
      name: z.string({ required_error: requiredMessage }).trim().min(1, requiredMessage),
      dateOfBirth: z
        .string({ required_error: requiredMessage })
        .min(1, requiredMessage)
        .superRefine((value, context) => {
          if (!value) return;

          const error = validateBirthDate(value);
          if (error) context.addIssue({ code: z.ZodIssueCode.custom, message: t(`addProfileForm.errors.${error}`, { years: PROFILE_MAX_AGE }) });
        }),
    })
  );

  const { errors, meta, defineField, handleSubmit, isFieldTouched, setFieldTouched } = useForm({
    validationSchema,
    initialValues: { name: '', dateOfBirth: '' },
  });

  const [name] = defineField('name');
  const [dateOfBirth] = defineField('dateOfBirth');

  const birthDateField = useProfileBirthDateField(dateOfBirth, () => setFieldTouched('dateOfBirth', true));

  const nameError = computed(() => (isFieldTouched('name') ? errors.value.name : ''));
  const dateOfBirthError = computed(() => (isFieldTouched('dateOfBirth') ? errors.value.dateOfBirth : ''));
  const isValid = computed(() => meta.value.valid);

  const birthDateSummary = computed(() => {
    if (errors.value.dateOfBirth) return '';

    const birthDate = describeBirthDate(dateOfBirth.value, getLanguageTag(locale.value));
    if (!birthDate) return '';

    const age = t(`addProfileForm.age.${birthDate.agePluralCategory}`, { count: birthDate.age });

    return t('addProfileForm.birthDateSummary', { date: birthDate.date, age });
  });

  const touchName = () => setFieldTouched('name', true);

  const closeForm = () => store.commit('handleAddProfile', false);

  const submit = handleSubmit((values) => {
    store.dispatch(actionTypes.createProfile, {
      name: values.name,
      avatar: DEFAULT_PROFILE_AVATAR,
      date_of_birth: toApiBirthDate(parseBirthDate(values.dateOfBirth)),
    });
    closeForm();
    router.push('/ProfilesGate');
  });

  return { name, dateOfBirth, nameError, dateOfBirthError, birthDateSummary, isValid, birthDateField, touchName, submit, closeForm };
};
